import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError, unwrap } from '@/api/envelope';
import {
  publishLink,
  removeLink,
  saveLink,
  unpublishLink,
  type LinkReply,
} from '@/api/generated';
import { fieldErrorsOf } from '@/api/serverErrors';
import { useReadOnly } from '@/auth/useViewingAs';
import { arcKey, useArcOrganizationId } from '@/lib/arcDraft';
import { serverSentence } from '@/lib/serverSentence';

import { linksKey } from './keys';
import type { LinkKind } from './linkState';

/** The refusal of the native volunteer form until it exists (backend D6). */
const VOLUNTEER_FORM_UNAVAILABLE = 'The in-app volunteer form is not available yet';

/** Refused before any request while Activist staff are viewing (P20). */
const VIEWING_REFUSAL = 'Nothing can be changed while viewing an organization.';

/** What a failed write says when the server gives no sentence, one per action (P17). */
export const WRITE_FALLBACK = {
  save: 'The link could not be saved. Please try again.',
  publish: 'The link could not be published. Please try again.',
  unpublish: 'The link could not be unpublished. Please try again.',
  remove: 'The link could not be removed. Please try again.',
} as const;

/**
 * Where a refused write is shown (P17, P34, P40): on the address field, on the
 * volunteer method, inline with the write controls disabled, inline above the
 * actions, in a toast, or nowhere on the page because the session handler
 * says it (a 401).
 */
export interface PlacedRefusal {
  kind: 'field' | 'mode' | 'forbidden' | 'conflict' | 'toast' | 'elsewhere';
  message: string;
}

/**
 * Sorts a refused link write by where the page shows it
 * (`docs/error-contract.md`: 400 and 422 "inline on the field the sentence
 * names"). A 400, the missing-header sentence, and a 422 that names neither
 * the address nor the method name no field on the page, so they are a toast
 * (P40).
 */
export function classifyLinkError(
  error: unknown,
  fallback: string = WRITE_FALLBACK.save,
): PlacedRefusal {
  if (!(error instanceof ApiError)) {
    return { kind: 'toast', message: serverSentence(error, fallback) };
  }
  const message = error.serverMessage ?? error.message;
  if (error.status === 401) return { kind: 'elsewhere', message };
  if (error.status === 422) {
    const problems = fieldErrorsOf(error);
    const mode = problems.find((problem) => problem.field === 'mode');
    if (mode) return { kind: 'mode', message: mode.message };
    const url = problems.find((problem) => problem.field === 'url');
    if (url) return { kind: 'field', message: url.message };
    return { kind: 'toast', message: serverSentence(error, fallback) };
  }
  if (error.status === 403) return { kind: 'forbidden', message };
  if (error.status === 409) {
    // Any other 409 is the publish refusal of a link with no draft address,
    // "Add a link before publishing" (backend D7), and is shown with its
    // sentence, whatever its code (P40).
    return {
      kind: error.apiError === VOLUNTEER_FORM_UNAVAILABLE ? 'mode' : 'conflict',
      message,
    };
  }
  return { kind: 'toast', message: serverSentence(error, fallback) };
}

/**
 * Save Draft, Publish, Unpublish and Remove link for one kind, plus Publish
 * Changes with unsaved edits (save, then publish, as one action).
 *
 * Each write that lands hands its reply to `onWritten` (the page resets its
 * form to it) and then asks again for the two reads it changed, the links and
 * the ARC draft the builder shows them in, and nothing else (P12, I4). No
 * cache is written by hand and a refused write changes nothing (NF4). `pending`
 * is true while any of them is in flight, so the page sends one at a time
 * (P18, NF6). While Activist staff are viewing, every write is refused before
 * it is sent, as the ARC Builder's are (P20).
 */
export function useLinkWrites(kind: LinkKind, onWritten?: (link: LinkReply) => void) {
  const queryClient = useQueryClient();
  const readOnly = useReadOnly();
  const organizationId = useArcOrganizationId();

  /** The two reads a link write changes, and nothing else (P12, I4). */
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: linksKey(organizationId) }),
      queryClient.invalidateQueries({ queryKey: arcKey(organizationId) }),
    ]);

  const written = async (link: LinkReply) => {
    onWritten?.(link);
    await refresh();
  };

  const send = async ({ url }: { url: string }) => {
    if (readOnly) throw new Error(VIEWING_REFUSAL);
    const reply = await saveLink({
      path: { kind },
      // Every Volunteer save names its mode; NATIVE is never sent (P9, I5).
      body: kind === 'volunteer' ? { url, mode: 'EXTERNAL' } : { url },
      throwOnError: true,
    });
    return unwrap(reply.data);
  };

  const sendPublish = async () => {
    if (readOnly) throw new Error(VIEWING_REFUSAL);
    const reply = await publishLink({ path: { kind }, throwOnError: true });
    return unwrap(reply.data);
  };

  const save = useMutation({ mutationFn: send, onSuccess: written });

  const publish = useMutation({ mutationFn: sendPublish, onSuccess: written });

  const unpublish = useMutation({
    mutationFn: async () => {
      if (readOnly) throw new Error(VIEWING_REFUSAL);
      const reply = await unpublishLink({ path: { kind }, throwOnError: true });
      return unwrap(reply.data);
    },
    onSuccess: written,
  });

  const remove = useMutation({
    mutationFn: async () => {
      if (readOnly) throw new Error(VIEWING_REFUSAL);
      const reply = await removeLink({ path: { kind }, throwOnError: true });
      return unwrap(reply.data);
    },
    onSuccess: written,
  });

  /*
   * The saved draft is a write of its own: once it lands it is handed over,
   * and a refused publish after it still asks for both reads again, so the
   * page shows the draft that was saved (I4). A refused save publishes
   * nothing.
   */
  const saveAndPublish = useMutation({
    mutationFn: async (values: { url: string }) => {
      // Sent before `onWritten?.()`, which would skip its argument without a callback.
      const saved = await send(values);
      onWritten?.(saved);
      try {
        return await sendPublish();
      } catch (error) {
        await refresh();
        throw error;
      }
    },
    onSuccess: written,
  });

  return {
    save,
    publish,
    unpublish,
    remove,
    saveAndPublish,
    pending:
      save.isPending ||
      publish.isPending ||
      unpublish.isPending ||
      remove.isPending ||
      saveAndPublish.isPending,
  };
}
